import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { loadStripe } from '@stripe/stripe-js';
import authFetch from '../utils/authFetch';
import { Elements } from '@stripe/react-stripe-js';
import { useToast } from '../components/ToastProvider';
import { trackSubscriptionCheckoutStarted } from '../utils/productAnalytics';
import {
    RocketLaunchIcon,
    StarIcon,
    ShieldCheckIcon,
} from '@heroicons/react/24/outline'; 

const stripePromise = loadStripe('pk_live_51Pdd5hRrJ2IEm8V5HhU0bRzo5NaDfTuk89unIlU3ZOeF50pfpaIBPl35dg84juaaULjlsvXQKFbziEMEYlJFCM2b00msjtlVAO');

const Subscription = () => {
    const userId = useSelector((state) => state.auth.user.id);
    const token = useSelector((state) => state.auth.token);
    const toast = useToast();

    const [isLoading, setIsLoading] = useState(false);

    const handleClick = async (type, { amount, credits } = {}) => {
        trackSubscriptionCheckoutStarted();
        setIsLoading(true);
        const payAmount = amount ?? 10;
        const creditsToAdd = credits || payAmount;

        try {
            const endpoint = type === 'subscription'
                ? 'https://cvanalyzerback.osc-fr1.scalingo.io/api/stripe/create-checkout-session'
                : 'https://cvanalyzerback.osc-fr1.scalingo.io/api/stripe/create-credit-purchase-session';

            const response = await authFetch(endpoint, {
                method: 'POST',
                body: JSON.stringify({
                    userId,
                    amount: payAmount,
                    credits: creditsToAdd
                }),
            });

            if (!response.ok) {
                throw new Error('Échec de la création de la session de paiement');
            }

            const session = await response.json();
            const stripe = await stripePromise;

            const { error } = await stripe.redirectToCheckout({
                sessionId: session.id,
            });

            if (error) {
                toast.error(
                    'Redirection impossible',
                    error.message ||
                        'Une erreur est survenue lors de la redirection vers le paiement. Veuillez réessayer.'
                );
            }
        } catch (error) {
            toast.error(
                'Paiement',
                error?.message ||
                    'Impossible de préparer la session de paiement. Vérifiez votre connexion et réessayez.'
            );
        } finally {
            setIsLoading(false);
        }
    };

    const creditPackages = [
        { amount: 5, credits: 5, popular: false, label: 'Starter', promise: 'Rapport + CV restructuré' },
        { amount: 10, credits: 12, popular: true, bonus: '+2 crédits', label: 'Essentiel', promise: 'Rapport, CV et lettre' },
        { amount: 20, credits: 26, popular: false, bonus: '+6 crédits', label: 'Pro', promise: 'Multi-candidatures' },
        { amount: 50, credits: 70, popular: false, bonus: '+20 crédits', label: 'Expert', promise: 'Usage intensif' },
    ];

    return (
        <Elements stripe={stripePromise}>
            <div className="page-shell overflow-x-hidden">
                <Helmet>
                    <title>Débloquer le rapport | CV Analyse</title>
                    <meta name="robots" content="noindex, nofollow" />
                    <link rel="canonical" href="https://www.cv-analyse.fr/subscription" />
                </Helmet>
                <div className="page-wrap max-w-6xl">
                    <div className="page-header">
                        <div>
                            <h1 className="page-title">Débloquez votre rapport</h1>
                            <p className="page-subtitle">
                                Dès 5 € : importez à nouveau votre CV, l&apos;IA génère le rapport complet et le CV restructuré. Sans abonnement.
                            </p>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-4 mb-8">
                        <div className="card-surface p-5">
                            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                                <RocketLaunchIcon className="w-6 h-6 text-white" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-800 mb-2">Analyse IA complète</h3>
                            <p className="text-gray-600 text-sm">
                                Après paiement, réimportez le CV : détail du score, recommandations, ATS et CV à copier
                            </p>
                        </div>

                        <div className="card-surface p-5">
                            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                                <ShieldCheckIcon className="w-6 h-6 text-white" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-800 mb-2">Paiement sécurisé</h3>
                            <p className="text-gray-600 text-sm">
                                Transactions sécurisées via Stripe, vos données sont protégées
                            </p>
                        </div>

                        <div className="card-surface p-5">
                            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                                <StarIcon className="w-6 h-6 text-white" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-800 mb-2">Sans engagement</h3>
                            <p className="text-gray-600 text-sm">
                                Achetez des crédits à la demande, sans abonnement obligatoire
                            </p>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
                        {creditPackages.map((pkg, index) => (
                            <div
                                key={index}
                                className={`card-surface p-6 text-center ${
                                    pkg.popular ? 'border-blue-600' : ''
                                }`}
                            >
                                {pkg.popular && (
                                    <p className="text-xs font-medium text-blue-700 mb-2">Le plus choisi</p>
                                )}
                                <p className="text-sm font-semibold text-gray-500 mb-1">{pkg.label}</p>
                                <div className="text-2xl font-semibold text-gray-900 mb-1">
                                    {pkg.amount} €
                                </div>
                                <h3 className="text-sm font-semibold text-gray-800 mb-2">
                                    {pkg.promise}
                                </h3>
                                <p className="text-xs text-gray-500 mb-1">{pkg.credits} crédits inclus</p>
                                {pkg.bonus ? (
                                    <p className="text-sm text-gray-600 mb-4">{pkg.bonus}</p>
                                ) : (
                                    <div className="mb-4" />
                                )}
                                <button
                                    type="button"
                                    onClick={() => {
                                        handleClick('credits', { amount: pkg.amount, credits: pkg.credits });
                                    }}
                                    disabled={isLoading}
                                    className={`w-full ${isLoading ? 'bg-gray-400 cursor-not-allowed py-2.5 px-5 rounded-lg text-white' : 'btn-primary w-full'}`}
                                >
                                    {isLoading ? 'Chargement...' : 'Acheter'}
                                </button>
                            </div>
                        ))}
                    </div>

                    <div className="card-surface p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Comment ça marche</h3>
                        <p className="text-gray-600 text-sm mb-3">
                            L&apos;aperçu (score + 3 conseils) est offert, sans IA. Dès 5 €, réimportez le CV : l&apos;IA génère le rapport complet et le CV restructuré.
                        </p>
                        <ul className="text-sm text-gray-600 space-y-1">
                            <li>Starter 5 € : rapport complet + CV à copier, relances ensuite</li>
                            <li>Essentiel 10 € : rapport, CV, et de quoi générer une lettre</li>
                            <li>Assistant IA : 1 crédit par message</li>
                            <li>Paiement sécurisé via Stripe</li>
                        </ul>
                    </div>
                </div>
            </div>
        </Elements>
    );
};

export default Subscription;
